import { GeneratedProductImage, Product } from "@/shared/types/product"

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null

const normalizeGeneratedImage = (
  value: unknown,
  fallbackUrl: string,
  fallbackIndex: number,
  imageUrls: string[],
  fallbackPromptId?: string
): GeneratedProductImage => {
  const sourceImageIndex =
    isRecord(value) && typeof value.sourceImageIndex === "number" && value.sourceImageIndex >= 0
      ? value.sourceImageIndex
      : Math.min(fallbackIndex, Math.max(0, imageUrls.length - 1))

  return {
    url: isRecord(value) && typeof value.url === "string" && value.url ? value.url : fallbackUrl,
    sourceImageIndex,
    sourceImageUrl:
      (isRecord(value) && typeof value.sourceImageUrl === "string" && value.sourceImageUrl) ||
      imageUrls[sourceImageIndex] ||
      imageUrls[0],
    promptId: (isRecord(value) && typeof value.promptId === "string" && value.promptId) || fallbackPromptId || null,
    promptText: (isRecord(value) && typeof value.promptText === "string" && value.promptText) || "",
    status: isRecord(value) && value.status === "regenerating" ? "regenerating" : "ready",
    createdAt: (isRecord(value) && (value.createdAt as GeneratedProductImage["createdAt"])) || null,
    regeneratedAt: (isRecord(value) && (value.regeneratedAt as GeneratedProductImage["regeneratedAt"])) || null,
    regenerationRequestedAt:
      (isRecord(value) && (value.regenerationRequestedAt as GeneratedProductImage["regenerationRequestedAt"])) || null
  }
}

const getGeneratedProductImages = (
  product: Pick<Product, "generatedImages" | "generatedImageUrls" | "imageUrls" | "selectedPromptIds">
) => {
  const imageUrls = Array.isArray(product.imageUrls) ? product.imageUrls.filter(Boolean) : []
  const generatedImageUrls = Array.isArray(product.generatedImageUrls) ? product.generatedImageUrls.filter(Boolean) : []
  const generatedImages = Array.isArray(product.generatedImages) ? product.generatedImages : []

  if (generatedImages.length > 0) {
    return generatedImages
      .map((image, index) =>
        normalizeGeneratedImage(
          image,
          generatedImageUrls[index] ?? "",
          index,
          imageUrls,
          product.selectedPromptIds?.[index]
        )
      )
      .filter(image => Boolean(image.url))
  }

  return generatedImageUrls.map((url, index) =>
    normalizeGeneratedImage({ url }, url, index, imageUrls, product.selectedPromptIds?.[index])
  )
}

const normalizeProduct = (product: Product): Product => {
  return {
    ...product,
    generatedImageUrls: Array.isArray(product.generatedImages)
      ? product.generatedImages.map(image => image.url)
      : Object.values(product.generatedImages || {}).map(image => (image as GeneratedProductImage).url)
  }
}

export { getGeneratedProductImages, normalizeProduct }
